import { Component, OnInit } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, FormRecord, Validators } from "@angular/forms";
import { RequestCreationFormService } from "@fiduciaria/RequestCreation/Services/request-creation-form.service";
import { TrustService } from "@fiduciaria/Shared/Services/trust.service";
import { BizagiOption, Currency, IdentificationType } from "@fiduciaria/Shared/Types/bizagi-option.interface";
import { AdditionalField } from "@fiduciaria/Shared/Types/escrow.interface";
import { RequiredInfo } from "@fiduciaria/Shared/Types/required-info.interface";
import { Observable, shareReplay } from "rxjs";

import { Unsubscriber } from "@shared/Components/Unsubscriber/unsubscriber.component";

import { fiduciaryIdentificationType } from "../../Types/Interfaces/fiduciary-identification-type.enum";

@Component({
  selector: "app-request-data-form",
  templateUrl: "./request-data-form.component.html",
})
export class RequestDataFormComponent extends Unsubscriber implements OnInit {
  public currencies$: Observable<Currency[]>;
  public stages$: Observable<BizagiOption[]>;
  public identificationTypes$: Observable<IdentificationType[]>;
  public customerTypes$: Observable<BizagiOption[]>;
  public additionalInformationsFields: AdditionalField[];
  public fiduciaryIdentificationType = fiduciaryIdentificationType;

  public form: FormGroup = this.formBuilder.group({
    description: [null, Validators.required],
    amount: [null, Validators.min(0)],
    currency: [null],
    invoiceQuantity: [null],
    stage: [null],
    customerName: [null],
    identificationType: [null],
    identification: [null],
    customerType: [null],
  });

  public showInvoicesForm: boolean;
  public showClientForm: boolean;
  public isStagesRequired: boolean;
  public isCurrencyRequired: boolean;
  public currentIdentificationType: IdentificationType;

  public requiredInfo: RequiredInfo;

  constructor(
    private formBuilder: FormBuilder,
    private requestCreationForm: RequestCreationFormService,
    private trustService: TrustService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.currencies$ = this.trustService.getCurrencies();
    this.stages$ = this.trustService.getStages().pipe(shareReplay(1));
    this.identificationTypes$ = this.trustService.getIdentificationTypes().pipe(shareReplay(1));
    this.customerTypes$ = this.trustService.getCustomerTypes().pipe(shareReplay(1));

    this.updateRequiredFields();
    this.setAdditionalInformationFields();

    this.anotherSubscription = this.requestCreationForm.reset.subscribe(() => {
      this.form.reset();
    });

    this.identificationTypeControl.valueChanges.subscribe((value) => (this.currentIdentificationType = value));
  }

  public updateRequiredFields(): void {
    this.anotherSubscription = this.requestCreationForm.requiredInfo.subscribe((value) => {
      this.form.reset();
      this.requiredInfo = value;
      this.showInvoicesForm = value.showInvoiceQuantity || value.showAmount || value.showCurrency;
      this.isStagesRequired = value.showStage;
      this.isCurrencyRequired = value.showCurrency;
      this.form.get("amount")?.setValidators(value.showAmount ? [Validators.required, Validators.min(0)] : []);
      this.form.get("currency")?.setValidators(value.showCurrency ? [Validators.required] : []);
      this.form.get("invoiceQuantity")?.setValidators(value.showInvoiceQuantity ? [Validators.required] : []);
      this.form.get("stage")?.setValidators(value.showStage ? [Validators.required] : []);
      this.updateFields(["amount", "currency", "invoices", "stage"]);
    });

    this.anotherSubscription = this.requestCreationForm.requiredInfo.subscribe((value) => {
      this.showClientForm = value.showCustomer;
      const validator = value.showCustomer ? Validators.required : [];
      this.form.get("customerName")?.setValidators(validator);
      this.form.get("identificationType")?.setValidators(validator);
      this.form.get("identification")?.setValidators(validator);
      this.form.get("customerType")?.setValidators(validator);
      this.updateFields(["customerName", "identificationType", "identification", "customerType"]);
    });
  }

  public updateFields(fields: string[]): void {
    fields.forEach((field) => {
      this.form.get(field)?.updateValueAndValidity();
    });
  }

  public setAdditionalInformationFields(): void {
    this.requestCreationForm.additionalRequiredFields$.subscribe((additionalRequiredFields) => {
      const informationsControl = this.formBuilder.record({});
      additionalRequiredFields.forEach((field) => {
        informationsControl.addControl(String(field.sCode), this.formBuilder.control(null, Validators.required));
      });

      this.form.setControl("informations", informationsControl);
      this.additionalInformationsFields = additionalRequiredFields;
    });
  }

  public get additionalInfoRecord(): FormRecord {
    return this.form.get("informations") as FormRecord;
  }

  public get identificationTypeControl(): AbstractControl {
    return this.form.get("identificationType");
  }

  public onSubmit(): void {
    const informations = Object.entries(this.additionalInfoRecord.value).map(([key, value], index) => ({
      sCode: key,
      value: value,
      nameInformation: this.additionalInformationsFields[index].nameInformation,
    }));
    this.requestCreationForm.addValue({ ...this.form.value, informations });
  }
}
